import { DEFAULT_DESIGN, isDesignId, type DesignId } from './designs.js';

export const DESIGN_STORAGE_PREFIX = 'artosera:design:v1:';
export function designStorageKey(compteId: string | null): string {
  return compteId === null
    ? `${DESIGN_STORAGE_PREFIX}anonymous`
    : `${DESIGN_STORAGE_PREFIX}user:${compteId}`;
}
// If storage is denied or full, the current page still honours a selection.
// Account-specific keys prevent another user from inheriting this fallback.
const volatile = new Map<string, DesignId>();
const listeners = new Set<() => void>();
function announce(): void {
  for (const listener of listeners) listener();
}
/** The account's own choice in this browser, or `null` when it has none. */
export function readDesignPreference(compteId: string | null): DesignId | null {
  const key = designStorageKey(compteId);
  if (volatile.has(key)) return volatile.get(key) ?? null;
  try {
    const value = window.localStorage.getItem(key);
    return isDesignId(value) ? value : null;
  } catch {
    return null;
  }
}
export function readDesign(compteId: string | null): DesignId {
  return readDesignPreference(compteId) ?? DEFAULT_DESIGN;
}

/**
 * Lot C — the current gallery's theme policy, set by the console and read
 * from the session: its theme, and whether its users may pick another one.
 * Outside a gallery nothing is imposed.
 */
export interface PolitiqueTheme {
  readonly theme: DesignId;
  readonly libre: boolean;
}
export const POLITIQUE_LIBRE: PolitiqueTheme = { theme: DEFAULT_DESIGN, libre: true };
let politique: PolitiqueTheme = POLITIQUE_LIBRE;
export function lirePolitiqueTheme(): PolitiqueTheme {
  return politique;
}
export function definirPolitiqueTheme(suivante: PolitiqueTheme): void {
  if (suivante.theme === politique.theme && suivante.libre === politique.libre) return;
  politique = suivante;
  announce();
}
/**
 * The design actually applied: the console's theme when the choice is locked
 * (local preference and links ignored); otherwise the account's preference,
 * the console's theme serving as the default.
 */
export function designEffectif(compteId: string | null): DesignId {
  if (!politique.libre) return politique.theme;
  return readDesignPreference(compteId) ?? politique.theme;
}
export function writeDesign(compteId: string | null, value: unknown): void {
  if (!isDesignId(value)) return;
  const key = designStorageKey(compteId);
  try {
    window.localStorage.setItem(key, value);
    volatile.delete(key);
  } catch {
    volatile.set(key, value);
  }
  announce();
}
export function subscribeDesign(listener: () => void): () => void {
  listeners.add(listener);
  function onStorage(event: StorageEvent): void {
    if (event.key === null || event.key.startsWith(DESIGN_STORAGE_PREFIX)) {
      if (event.key === null) volatile.clear();
      else volatile.delete(event.key);
      listener();
    }
  }
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}
export function applyDesign(design: DesignId): void {
  if (typeof document !== 'undefined') document.documentElement.dataset.design = design;
}
/** Called before React; no previous user's identity is guessed on a login page. */
export function initializeDesign(): void {
  applyDesign(DEFAULT_DESIGN);
}
